import React, { Component } from "react";

import styled from "styled-components/macro";

import {
  Box,
  IconButton,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from "@material-ui/core";

import { RemoveRedEye as RemoveRedEyeIcon } from "@material-ui/icons";

import { Link } from "react-router-dom";
import { spacing } from "@material-ui/system";

import { getPayments } from "../services/paymentServices";
import { withTranslation } from "react-i18next";
import {
  formatCurrency,
  formatDate,
  formatNumber,
} from "../utils/localization";

const Paper = styled(MuiPaper)(spacing);

const TableShell = styled(Paper)`
  border-radius: 0 0 26px 26px;
  box-shadow: none;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
`;

const HeaderCell = styled(TableCell)`
  && {
    background: #f8fbff;
    color: #475569;
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    border-bottom: 1px solid rgba(226, 232, 240, 0.9);
    white-space: nowrap;
  }
`;

const BodyCell = styled(TableCell)`
  && {
    padding-top: 18px;
    padding-bottom: 18px;
    border-bottom: 1px solid rgba(241, 245, 249, 1);
    color: #0f172a;
  }
`;

const ReferenceCell = styled(BodyCell)`
  && {
    font-weight: 700;
  }
`;

const DataRow = styled(TableRow)`
  && {
    transition: transform 0.18s ease, box-shadow 0.18s ease,
      background 0.18s ease;
  }

  &:hover {
    background: rgba(239, 246, 255, 0.7);
    transform: translateY(-1px);
    box-shadow: inset 0 0 0 1px rgba(186, 230, 253, 0.7);
  }
`;

const ValuePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${(props) => props.bg || "#f8fafc"};
  color: ${(props) => props.color || "#0f172a"};
  font-weight: 700;
  line-height: 1;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${(props) => props.bg};
  color: ${(props) => props.color};
  font-weight: 700;
  white-space: nowrap;
`;

const ActionButton = styled(IconButton)`
  && {
    width: 38px;
    height: 38px;
    background: #eff6ff;
    color: #2563eb;
  }
`;

const headCells = [
  { id: "id", alignment: "right", translationKey: "payments.id" },
  { id: "reference", alignment: "left", translationKey: "payments.reference" },
  { id: "quantity", alignment: "left", translationKey: "payments.viewCount" },
  { id: "cost", alignment: "left", translationKey: "payments.cost" },
  { id: "grossPay", alignment: "left", translationKey: "payments.gross" },
  { id: "pay", alignment: "left", translationKey: "payments.net" },
  { id: "tax", alignment: "left", translationKey: "payments.tax" },
  { id: "retention", alignment: "left", translationKey: "payments.retention" },
  {
    id: "transactionStatus",
    alignment: "left",
    translationKey: "common.status",
  },
  { id: "actions", alignment: "center", translationKey: "payments.details" },
];

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function renderStatus(transactionStatus, t) {
  if (transactionStatus == null || transactionStatus.id === 2) {
    return (
      <StatusPill bg="#fff7ed" color="#c2410c">
        {t("payments.status.processing")}
      </StatusPill>
    );
  }

  if (transactionStatus.id === 1) {
    return (
      <StatusPill bg="#ecfdf5" color="#166534">
        {t("payments.status.paid")}
      </StatusPill>
    );
  }

  if (transactionStatus.id === 3) {
    return (
      <StatusPill bg="#fef2f2" color="#b91c1c">
        {t("payments.status.rejected")}
      </StatusPill>
    );
  }

  return (
    <StatusPill bg="#f8fafc" color="#475569">
      {t("payments.status.undefined")}
    </StatusPill>
  );
}

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, t } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <HeaderCell
            key={headCell.id}
            align={headCell.alignment}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {t(headCell.translationKey)}
            </TableSortLabel>
          </HeaderCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

class PaymentTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      order: "asc",
      orderBy: "reference",
      page: 0,
      rowsPerPage: 10,
    };
  }

  async getAllPayments() {
    const res = await getPayments();
    const data = res.data.filter(
      (payment) => payment.originSystem === "REFERRAL"
    );
    this.setState({ rows: data });
  }

  componentDidMount() {
    this.getAllPayments();
  }

  render() {
    const { t } = this.props;
    const handleRequestSort = (event, property) => {
      const isAsc =
        this.state.orderBy === property && this.state.order === "asc";
      this.setState({ order: isAsc ? "desc" : "asc", orderBy: property });
    };

    const handleChangePage = (event, newPage) => {
      this.setState({ page: newPage });
    };

    const handleChangeRowsPerPage = (event) => {
      this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 });
    };

    const emptyRows =
      this.state.rowsPerPage -
      Math.min(
        this.state.rowsPerPage,
        this.state.rows.length - this.state.page * this.state.rowsPerPage
      );

    return (
      <div>
        <TableShell>
          <TableContainer>
            <Table
              aria-labelledby="tableTitle"
              size="medium"
              aria-label={t("payments.history")}
            >
              <EnhancedTableHead
                order={this.state.order}
                orderBy={this.state.orderBy}
                onRequestSort={handleRequestSort}
                t={t}
              />
              <TableBody>
                {stableSort(
                  this.state.rows,
                  getComparator(this.state.order, this.state.orderBy)
                )
                  .slice(
                    this.state.page * this.state.rowsPerPage,
                    this.state.page * this.state.rowsPerPage +
                      this.state.rowsPerPage
                  )
                  .map((row, index) => (
                    <DataRow hover key={`${row.id}-${index}`}>
                      <BodyCell align="right">{row.id}</BodyCell>
                      <ReferenceCell align="left">
                        {row.reference}
                      </ReferenceCell>
                      <BodyCell align="left">
                        <ValuePill bg="#f8fafc" color="#334155">
                          {formatNumber(row.quantity)}
                        </ValuePill>
                      </BodyCell>
                      <BodyCell align="left">
                        <ValuePill bg="#eff6ff" color="#0f4c81">
                          {formatCurrency(row.cost)}
                        </ValuePill>
                      </BodyCell>
                      <BodyCell align="left">
                        <ValuePill bg="#f8fafc" color="#334155">
                          {formatCurrency(row.grossPay)}
                        </ValuePill>
                      </BodyCell>
                      <BodyCell align="left">
                        <ValuePill bg="#ecfdf5" color="#166534">
                          {formatCurrency(row.pay)}
                        </ValuePill>
                      </BodyCell>
                      <BodyCell align="left">
                        <ValuePill bg="#fff7ed" color="#c2410c">
                          {formatCurrency(row.tax)}
                        </ValuePill>
                      </BodyCell>
                      <BodyCell align="left">
                        <ValuePill bg="#fef2f2" color="#b91c1c">
                          {formatCurrency(row.retention)}
                        </ValuePill>
                      </BodyCell>
                      <BodyCell align="left">
                        {renderStatus(row.transactionStatus, t)}
                      </BodyCell>
                      <BodyCell padding="none" align="center">
                        <Box mr={2}>
                          <Link
                            to={{
                              pathname: "/invoiceDetail",
                              state: {
                                idInvoice: row.id,
                                quantity: row.quantity,
                                cost: row.cost,
                                grossPay: row.grossPay,
                                pay: row.pay,
                                tax: row.tax,
                                retention: row.retention,
                                reference: row.reference,
                                calculatedDate: row.calculatedDate
                                  ? formatDate(row.calculatedDate)
                                  : "",
                                payedDate: row.payedDate
                                  ? formatDate(row.payedDate)
                                  : "",
                              },
                            }}
                          >
                            <Tooltip title={t("payments.viewDetails")}>
                              <ActionButton aria-label="details">
                                <RemoveRedEyeIcon />
                              </ActionButton>
                            </Tooltip>
                          </Link>
                        </Box>
                      </BodyCell>
                    </DataRow>
                  ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={10} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={this.state.rows.length}
            rowsPerPage={this.state.rowsPerPage}
            page={this.state.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableShell>
      </div>
    );
  }
}

export default withTranslation()(PaymentTable);
